import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/categories";

const SHOPPABLE_CATEGORIES = CATEGORIES.filter((c) => c.id !== "all");
import { Plus, Edit2, Trash2, LogOut, Package, ListChecks, X, Settings as SettingsIcon, Save, MessageSquareQuote, Plug, RefreshCw, LayoutGrid, Instagram, Mail, FileText, Sparkles, Image as ImageIcon } from "lucide-react";
import ImageUpload from "../components/ImageUpload";
import { toast } from "sonner";
import { ReportsPanel } from "./admin/ReportsPanel";
import { BannersPanel } from "./admin/BannersPanel";

const EMPTY = {
  title: "", description: "", price: 0, category: "ai-prompts",
  image_url: "", images: [], is_digital: true, download_url: "", affiliate_url: "",
  shopify_url: "", video_url: "", podcast_url: "", external_checkout_url: "",
  platform: "", featured: false, tags: [],
  option_groups: [], variants: [],
};

export default function AdminDashboard() {
  const { user, logout, checking } = useAuth();
  const { settings, refresh: refreshSettings } = useSettings();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [siteForm, setSiteForm] = useState(settings);
  const [savingSite, setSavingSite] = useState(false);
  const [tEditing, setTEditing] = useState(null);
  const [tForm, setTForm] = useState({ name: "", source: "website", rating: 5, content: "", avatar_url: "", link: "", featured: true });
  const [intLoading, setIntLoading] = useState("");
  const [intResult, setIntResult] = useState(null);
  const [heroTiles, setHeroTiles] = useState([
    { position: 1, image_url: "", label: "", link: "", product_id: "" },
    { position: 2, image_url: "", label: "", link: "", product_id: "" },
    { position: 3, image_url: "", label: "", link: "", product_id: "" },
    { position: 4, image_url: "", label: "", link: "", product_id: "" },
  ]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [spEditing, setSpEditing] = useState(null);
  const [spForm, setSpForm] = useState({ platform: "instagram", url: "", image_url: "", caption: "", handle: "" });
  const [carts, setCarts] = useState([]);
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [wishlists, setWishlists] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [upsellStatus, setUpsellStatus] = useState(null);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [socialQuickUrl, setSocialQuickUrl] = useState("");
  const [socialQuickLoading, setSocialQuickLoading] = useState(false);

  async function refresh() {
    const [p, o, t, h, sp, ac, inq, wl] = await Promise.all([
      api.get("/products"), api.get("/orders"), api.get("/testimonials"), api.get("/hero-tiles"), api.get("/social-posts"),
      api.get("/cart/abandoned"), api.get("/inquiries"), api.get("/wishlist"),
    ]);
    setProducts(p.data);
    setOrders(o.data);
    setTestimonials(t.data);
    setSocialPosts(sp.data);
    setCarts(ac.data);
    setInquiries(inq.data || []);
    setWishlists(wl.data || []);
    if (h.data && h.data.length) {
      const next = [1, 2, 3, 4].map((pos) => {
        const found = h.data.find((x) => x.position === pos);
        return found ? {
          position: pos,
          image_url: found.image_url || "",
          label: found.label || "",
          link: found.link || "",
          product_id: found.product_id || "",
        } : { position: pos, image_url: "", label: "", link: "", product_id: "" };
      });
      setHeroTiles(next);
    }
  }
  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);
  useEffect(() => { setSiteForm(settings); }, [settings]);
  useEffect(() => {
    if (tab === "recovery" && user?.role === "admin") loadUpsellStatus();
  }, [tab, user]);

  if (checking) return null;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  function startNew() { setEditing("new"); setForm(EMPTY); }
  function startEdit(p) { setEditing(p.id); setForm({ ...EMPTY, ...p, tags: p.tags || [], images: p.images || [], option_groups: p.option_groups || [], variants: p.variants || [] }); }

  async function save(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        external_checkout_url: (form.external_checkout_url || "").trim() || null,
        price: parseFloat(form.price) || 0,
        images: (form.images || []).map((s) => (s || "").trim()).filter(Boolean),
        option_groups: form.option_groups || [],
        variants: (form.variants || []).map((v) => ({
          ...v,
          price: v.price === "" || v.price === undefined ? null : (typeof v.price === "number" ? v.price : parseFloat(v.price)),
          stock: v.stock === "" || v.stock === undefined || v.stock === null ? null : (typeof v.stock === "number" ? v.stock : parseInt(v.stock, 10)),
        })),
      };
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing}`, payload);
      toast.success("Saved.");
      setEditing(null);
      refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join(" ") : (typeof d === "string" ? d : "Save failed.");
      toast.error(msg);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted.");
    refresh();
  }

  async function setStatus(o, status) {
    await api.put(`/orders/${o.id}`, { status });
    toast.success(`Order marked ${status}.`);
    refresh();
  }

  async function fulfillPrintful(o) {
    try {
      const { data } = await api.post(`/orders/${o.id}/fulfill-printful`);
      if (data.already_fulfilled) toast.info(`Already on Printful #${data.printful_order_id}`);
      else toast.success(`Sent to Printful #${data.printful_order_id || ""}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send to Printful.");
    }
  }

  async function resendDownload(o) {
    try {
      const { data } = await api.post(`/orders/${o.id}/resend-download`);
      if (data.sent) toast.success(`Download email re-sent to ${data.to}`);
      else toast.error(`Email NOT sent: ${data.reason || "unknown"} (check Resend key + product Download URL)`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend.");
    }
  }

  async function saveSite(e) {
    e.preventDefault();
    setSavingSite(true);
    try {
      await api.put("/settings", siteForm);
      await refreshSettings();
      toast.success("Site settings updated. Reload other tabs to see changes everywhere.");
    } catch { toast.error("Save failed."); }
    finally { setSavingSite(false); }
  }

  function startNewT() { setTEditing("new"); setTForm({ name: "", source: "website", rating: 5, content: "", avatar_url: "", link: "", featured: true }); }
  function startEditT(t) { setTEditing(t.id); setTForm({ ...t }); }
  async function saveT(e) {
    e.preventDefault();
    try {
      if (tEditing === "new") await api.post("/testimonials", tForm);
      else await api.put(`/testimonials/${tEditing}`, tForm);
      toast.success("Saved.");
      setTEditing(null);
      refresh();
    } catch { toast.error("Save failed."); }
  }
  async function removeT(id) {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/testimonials/${id}`);
    refresh();
  }

  async function runIntegration(path, label) {
    setIntLoading(label);
    setIntResult(null);
    try {
      const { data } = await api.get(path);
      setIntResult({ label, data });
      if (data.ok) toast.success(`${label}: connected`);
      else toast.error(`${label}: ${data.error || "failed"}`);
    } catch (e) {
      toast.error(`${label}: request failed`);
    } finally {
      setIntLoading("");
    }
  }

  async function importCatalog(provider) {
    if (!window.confirm(`Import all ${provider} products into your shop catalog? Existing imports will be refreshed.`)) return;
    const label = `${provider[0].toUpperCase() + provider.slice(1)} import`;
    setIntLoading(label);
    setIntResult(null);
    try {
      const { data } = await api.post(`/integrations/${provider}/import`);
      setIntResult({ label, data });
      toast.success(`${label}: ${data.created} new, ${data.updated} updated (of ${data.fetched} fetched)`);
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "request failed";
      toast.error(`${label}: ${msg}`);
    } finally {
      setIntLoading("");
    }
  }

  async function addSocialFromUrl(e) {
    e.preventDefault();
    if (!socialQuickUrl.trim()) return;
    setSocialQuickLoading(true);
    try {
      await api.post("/social-posts/from-url", { url: socialQuickUrl.trim() });
      toast.success("Post added — live embed will render on the homepage.");
      setSocialQuickUrl("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add post.");
    } finally {
      setSocialQuickLoading(false);
    }
  }

  async function saveHeroTiles(e) {
    e.preventDefault();
    try {
      const payload = heroTiles
        .filter((t) => t.image_url)
        .map((t) => ({
          position: t.position,
          image_url: t.image_url,
          label: t.label || null,
          link: t.link || null,
          product_id: t.product_id || null,
        }));
      await api.put("/hero-tiles", payload);
      toast.success("Hero tiles saved.");
      refresh();
    } catch { toast.error("Save failed."); }
  }

  function setTile(idx, patch) {
    setHeroTiles((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  }

  function pickProductForTile(idx, productId) {
    const p = products.find((x) => x.id === productId);
    if (!p) return setTile(idx, { product_id: "" });
    setTile(idx, {
      product_id: p.id,
      image_url: p.image_url || "",
      label: p.title,
      link: `/product/${p.id}`,
    });
  }

  function startNewSP() { setSpEditing("new"); setSpForm({ platform: "instagram", url: "", image_url: "", caption: "", handle: settings.instagram_main || "" }); }
  function startEditSP(sp) { setSpEditing(sp.id); setSpForm({ ...sp }); }
  async function saveSP(e) {
    e.preventDefault();
    try {
      if (spEditing === "new") await api.post("/social-posts", spForm);
      else await api.put(`/social-posts/${spEditing}`, spForm);
      toast.success("Saved.");
      setSpEditing(null);
      refresh();
    } catch { toast.error("Save failed."); }
  }
  async function removeSP(id) {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/social-posts/${id}`);
    refresh();
  }

  async function sendRecovery() {
    if (!window.confirm("Send 10% off recovery emails to abandoned carts older than 24h?")) return;
    setRecoverLoading(true);
    try {
      const { data } = await api.post("/cart/abandoned/send-recovery");
      toast.success(`Recovery: sent ${data.sent} of ${data.total} (${data.failed} failed)`);
      refresh();
    } catch { toast.error("Failed."); }
    finally { setRecoverLoading(false); }
  }

  async function sendWishlistReminders() {
    if (!window.confirm("Send 'still thinking?' email to wishlist items 3+ days old?")) return;
    setWishlistLoading(true);
    try {
      const { data } = await api.post("/wishlist/send-reminders");
      toast.success(`Wishlist: sent ${data.sent} of ${data.candidates} customers (${data.failed} failed)`);
      refresh();
    } catch { toast.error("Failed."); }
    finally { setWishlistLoading(false); }
  }

  async function loadUpsellStatus() {
    try {
      const { data } = await api.get("/admin/upsell/status");
      setUpsellStatus(data);
    } catch (err) {
      console.warn("[admin] failed to load upsell status:", err);
    }
  }

  async function runUpsellPass() {
    if (!window.confirm("Send 15% off upsell emails to all customers whose orders are 3+ days old?")) return;
    setUpsellLoading(true);
    try {
      const { data } = await api.post("/admin/upsell/run");
      toast.success(`Upsell: sent ${data.sent}, skipped ${data.skipped}, failed ${data.failed}`);
      loadUpsellStatus();
    } catch { toast.error("Failed."); }
    finally { setUpsellLoading(false); }
  }

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="uppercase text-xs tracking-[0.25em] text-[var(--accent)]">Admin</div>
          <h1 className="font-serif text-4xl mt-2">Dashboard</h1>
          <div className="text-sm text-[#5C5547] mt-1">Logged in as {user.email}</div>
        </div>
        <button onClick={logout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F5F2EA] hover:bg-[#EAE4D3] text-sm">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      <div className="flex gap-2 mb-8 flex-wrap">
        <TabBtn id="products" tab={tab} setTab={setTab} icon={Package} label={`Products (${products.length})`} />
        <TabBtn id="orders" tab={tab} setTab={setTab} icon={ListChecks} label={`Orders (${orders.length})`} />
        <TabBtn id="inquiries" tab={tab} setTab={setTab} icon={Sparkles} label={`Inquiries (${inquiries.filter((i) => i.status === "new").length})`} />
        <TabBtn id="testimonials" tab={tab} setTab={setTab} icon={MessageSquareQuote} label={`Testimonials (${testimonials.length})`} />
        <TabBtn id="social" tab={tab} setTab={setTab} icon={Instagram} label={`Social Posts (${socialPosts.length})`} />
        <TabBtn id="hero" tab={tab} setTab={setTab} icon={LayoutGrid} label="Hero Tiles" />
        <TabBtn id="banners" tab={tab} setTab={setTab} icon={ImageIcon} label="Banners" />
        <TabBtn id="content" tab={tab} setTab={setTab} icon={FileText} label="Content & Pages" />
        <TabBtn id="recovery" tab={tab} setTab={setTab} icon={Mail} label={`Cart Recovery (${carts.filter((c) => !c.recovered).length})`} />
        <TabBtn id="reports" tab={tab} setTab={setTab} icon={FileText} label="Reports" />
        <TabBtn id="integrations" tab={tab} setTab={setTab} icon={Plug} label="Integrations" />
        <TabBtn id="settings" tab={tab} setTab={setTab} icon={SettingsIcon} label="Site Settings" />
      </div>

      {tab === "products" && (
        <div>
          <div className="flex justify-end mb-6">
            <button onClick={startNew} data-testid="add-product-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent)] text-white hover:opacity-90 text-sm">
              <Plus className="w-4 h-4" /> New product
            </button>
          </div>

          <div className="bg-[#FDFBF7] rounded-3xl border border-[#EAE4D3] overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F5F2EA] uppercase text-[10px] tracking-[0.2em] text-[#5C5547]">
              <div className="col-span-5">Title</div>
              <div className="col-span-3">Category</div>
              <div className="col-span-2">Price</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-row-${p.id}`}
                className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-[#EAE4D3] items-center">
                <div className="col-span-5 flex items-center gap-3">
                  {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                  <div className="text-sm font-medium">{p.title}</div>
                  {p.external_checkout_url && (
                    <span data-testid={`publishing-studio-badge-${p.id}`}
                      className="px-2 py-0.5 rounded-full bg-[var(--accent)]/15 text-[var(--accent)] text-[9px] uppercase tracking-[0.15em] whitespace-nowrap">
                      Publishing Studio
                    </span>
                  )}
                </div>
                <div className="col-span-3 text-sm text-[#5C5547]">{p.category}</div>
                <div className="col-span-2 text-sm">${p.price.toFixed(2)}</div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button onClick={() => startEdit(p)} data-testid={`edit-${p.id}`}
                    className="p-2 rounded-lg hover:bg-[#F5F2EA]"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-${p.id}`}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-[#FDFBF7] rounded-3xl border border-[#EAE4D3] overflow-hidden">
          {orders.length === 0 && <div className="p-10 text-center text-[#5C5547]">No orders yet.</div>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-row-${o.id}`}
              className="px-6 py-4 border-b last:border-b-0 border-[#EAE4D3] flex flex-wrap items-center gap-4 justify-between">
              <div className="min-w-0">
                <div className="font-medium">{o.product_title}{o.variant_name ? ` — ${o.variant_name}` : ""}</div>
                <div className="text-xs text-[#5C5547]">
                  {o.customer_name} · {o.customer_email} · via {o.payment_method}
                  {o.payment_reference && ` · ref: ${o.payment_reference}`}
                </div>
                {o.shipping_address?.line1 && (
                  <div className="text-[11px] text-[#5C5547] mt-1" data-testid={`ship-${o.id}`}>
                    Ship to: {o.shipping_address.line1}{o.shipping_address.line2 ? `, ${o.shipping_address.line2}` : ""}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}, {o.shipping_address.country}
                  </div>
                )}
                {o.engraving && (
                  <div className="text-[11px] text-[var(--accent)] mt-1 font-serif italic" data-testid={`engraving-${o.id}`}>
                    Engrave: "{o.engraving}"
                  </div>
                )}
                <div className="text-xs mt-1">
                  Status: <span className={`uppercase tracking-[0.15em] ${o.status === "paid" ? "text-[var(--accent)]" : o.status === "fulfilled" ? "text-green-700" : "text-[#5C5547]"}`}>{o.status}</span>
                  {o.printful_order_id && <span className="ml-3 text-[10px] text-green-700">printful #{o.printful_order_id}</span>}
                  {o.printful_error && <span className="ml-3 text-[10px] text-red-600">printful error: {o.printful_error}</span>}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {o.status === "pending" && (
                  <button onClick={() => setStatus(o, "paid")} data-testid={`mark-paid-${o.id}`}
                    className="px-4 py-2 rounded-full bg-[var(--accent)] text-white text-xs hover:opacity-90">Mark paid</button>
                )}
                {(o.status === "paid" || o.status === "fulfilled") && o.variant_id && !o.printful_order_id && (
                  <button onClick={() => fulfillPrintful(o)} data-testid={`fulfill-printful-${o.id}`}
                    className="px-4 py-2 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs">Send to Printful</button>
                )}
                {o.status === "paid" && !o.variant_id && (
                  <button onClick={() => setStatus(o, "fulfilled")} data-testid={`mark-fulfilled-${o.id}`}
                    className="px-4 py-2 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs">Mark fulfilled</button>
                )}
                {(o.status === "paid" || o.status === "fulfilled") && (
                  <button onClick={() => resendDownload(o)} data-testid={`resend-download-${o.id}`}
                    className="px-4 py-2 rounded-full bg-[#F5F2EA] border border-[var(--accent)] text-[#2C2924] text-xs hover:bg-[#EAE4D3]"
                    title="Resend the download email to the customer (use if the link is broken or they didn't receive it)">
                    Resend download
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "social" && (
        <div data-testid="social-tab">
          <div className="bg-[#FDFBF7] rounded-3xl thick-gold p-6 mb-6">
            <h3 className="font-serif text-2xl">Quick add by URL</h3>
            <p className="text-sm text-[#5C5547] mt-1">
              Paste an Instagram or TikTok post URL — we'll auto-fetch the thumbnail + caption (TikTok only) and save it. The homepage renders the real live embed.
            </p>
            <form onSubmit={addSocialFromUrl} className="mt-4 flex gap-2 flex-wrap">
              <input
                required
                value={socialQuickUrl}
                onChange={(e) => setSocialQuickUrl(e.target.value)}
                placeholder="https://www.instagram.com/p/...   or   https://www.tiktok.com/@user/video/..."
                data-testid="social-quick-url-input"
                className="flex-1 min-w-[280px] px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm"
              />
              <button
                type="submit"
                disabled={socialQuickLoading}
                data-testid="social-quick-add-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-60"
                style={{ background: "var(--accent-strong)", color: "#000" }}
              >
                <Plus className="w-4 h-4" /> {socialQuickLoading ? "Adding…" : "Auto-add"}
              </button>
            </form>
          </div>
          <div className="flex justify-end mb-6">
            <button onClick={startNewSP} data-testid="add-social-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent-strong)] text-white hover:opacity-90 text-sm">
              <Plus className="w-4 h-4" /> New post (manual)
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {socialPosts.map((sp) => (
              <div key={sp.id} data-testid={`social-row-${sp.id}`}
                className="rounded-2xl overflow-hidden bg-[#FDFBF7] thick-gold relative group">
                {sp.image_url && <img src={sp.image_url} alt="" className="w-full aspect-square object-cover" />}
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent text-white">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent-soft)]">{sp.platform} · {sp.handle}</div>
                  <div className="text-xs line-clamp-2 mt-1">{sp.caption}</div>
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => startEditSP(sp)} className="p-2 rounded-full bg-white/90 hover:bg-white"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => removeSP(sp.id)} className="p-2 rounded-full bg-white/90 hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {spEditing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <form onSubmit={saveSP} className="bg-[#FDFBF7] rounded-3xl border border-[#EAE4D3] w-full max-w-lg p-8 relative" data-testid="social-form">
            <button type="button" onClick={() => setSpEditing(null)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F5F2EA]"><X className="w-4 h-4" /></button>
            <h2 className="font-serif text-2xl mb-4">{spEditing === "new" ? "New social post" : "Edit social post"}</h2>
            <div className="space-y-3">
              <select value={spForm.platform} onChange={(e) => setSpForm({ ...spForm, platform: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm">
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
              </select>
              <input required placeholder="Post URL" value={spForm.url} onChange={(e) => setSpForm({ ...spForm, url: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <input placeholder="Image URL" value={spForm.image_url || ""} onChange={(e) => setSpForm({ ...spForm, image_url: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <input placeholder="Handle (e.g. @ThisThatForMe)" value={spForm.handle || ""} onChange={(e) => setSpForm({ ...spForm, handle: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <textarea rows={3} placeholder="Caption" value={spForm.caption || ""} onChange={(e) => setSpForm({ ...spForm, caption: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
            </div>
            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setSpEditing(null)} className="flex-1 py-3 rounded-full bg-[#F5F2EA] text-sm">Cancel</button>
              <button type="submit" className="flex-1 py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">Save</button>
            </div>
          </form>
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-3xl space-y-6">
          <ChangeAdminCredentials />
          <form onSubmit={saveSite} data-testid="site-settings-form"
            className="bg-[#FDFBF7] rounded-3xl border-2 border-[var(--accent)]/30 p-8">
            <h2 className="font-serif text-2xl">Site settings</h2>
            <p className="text-sm text-[#5C5547] mt-1">Customize your brand identity. Changes apply to the whole site.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <Field label="Brand name" testid="settings-brand-name">
              <input value={siteForm.brand_name} onChange={(e) => setSiteForm({ ...siteForm, brand_name: e.target.value })} className="input" />
            </Field>
            <Field label="Tagline" testid="settings-tagline">
              <input value={siteForm.tagline} onChange={(e) => setSiteForm({ ...siteForm, tagline: e.target.value })} className="input" />
            </Field>
            <Field label="Logo image (upload from your computer)" testid="settings-logo-url" full>
              <ImageUpload
                value={siteForm.logo_url || ""}
                onChange={(url) => setSiteForm({ ...siteForm, logo_url: url })}
                label="Upload logo"
                testid="logo-uploader"
              />
              <input value={siteForm.logo_url || ""} onChange={(e) => setSiteForm({ ...siteForm, logo_url: e.target.value })}
                placeholder="…or paste image URL" className="input mt-2" />
            </Field>
            <Field label="Heading font (Google Font)" testid="settings-heading-font">
              <input value={siteForm.heading_font} onChange={(e) => setSiteForm({ ...siteForm, heading_font: e.target.value })}
                placeholder="e.g. Fraunces, Cormorant Garamond, DM Serif Display" className="input" />
            </Field>
            <Field label="Body font (Google Font)" testid="settings-body-font">
              <input value={siteForm.body_font} onChange={(e) => setSiteForm({ ...siteForm, body_font: e.target.value })}
                placeholder="e.g. Manrope, Inter, DM Sans" className="input" />
            </Field>
            <Field label="Category-card font (Google Font)" testid="settings-category-font">
              <input value={siteForm.category_font || ""} onChange={(e) => setSiteForm({ ...siteForm, category_font: e.target.value })}
                placeholder="e.g. Fraunces, Italiana, Cormorant" className="input" />
            </Field>
            <Field label="Accent color (gold)" testid="settings-accent">
              <div className="flex gap-2 items-center">
                <input type="color" value={siteForm.accent_color} onChange={(e) => setSiteForm({ ...siteForm, accent_color: e.target.value })}
                  className="w-14 h-12 rounded-xl border border-[#EAE4D3] bg-[#F5F2EA]" />
                <input value={siteForm.accent_color} onChange={(e) => setSiteForm({ ...siteForm, accent_color: e.target.value })}
                  className="input flex-1" />
              </div>
            </Field>
            <Field label="Contact email" testid="settings-contact">
              <input value={siteForm.contact_email} onChange={(e) => setSiteForm({ ...siteForm, contact_email: e.target.value })} className="input" />
            </Field>
            <Field label="WhatsApp number (with country code, digits only)" testid="settings-whatsapp">
              <input value={siteForm.whatsapp_number || ""} onChange={(e) => setSiteForm({ ...siteForm, whatsapp_number: e.target.value.replace(/\D/g, "") })} placeholder="e.g. 13105551234" className="input" />
            </Field>

            <div className="sm:col-span-2 mt-4 pt-4 border-t border-[#EAE4D3]">
              <div className="uppercase text-[10px] tracking-[0.2em] text-[var(--accent)] mb-3">SEO &amp; Social sharing</div>
            </div>
            <Field label="SEO title (shown on Google &amp; browser tab)" testid="settings-seo-title">
              <input value={siteForm.seo_title || ""} onChange={(e) => setSiteForm({ ...siteForm, seo_title: e.target.value })} placeholder="e.g. This That For Me — Curated AI prompts &amp; aesthetic jewelry" className="input" />
            </Field>
            <Field label="SEO description (155 chars max)" testid="settings-seo-description">
              <input value={siteForm.seo_description || ""} maxLength={155} onChange={(e) => setSiteForm({ ...siteForm, seo_description: e.target.value })} placeholder="What the site sells in one line. Shows on Google search results." className="input" />
            </Field>
            <Field label="Open Graph image URL (1200×630, shows on Facebook/Pinterest/Twitter cards)" testid="settings-seo-image">
              <input value={siteForm.seo_image || ""} onChange={(e) => setSiteForm({ ...siteForm, seo_image: e.target.value })} placeholder="https://your-cdn.com/share-card.png" className="input" />
            </Field>
            <Field label="SEO keywords (comma separated, optional)" testid="settings-seo-keywords">
              <input value={siteForm.seo_keywords || ""} onChange={(e) => setSiteForm({ ...siteForm, seo_keywords: e.target.value })} placeholder="e.g. ai prompts, ebooks, jewelry, creator merch" className="input" />
            </Field>
            <Field label="Production site URL (used in sitemap.xml — submit to Google Search Console)" testid="settings-site-url" full>
              <input data-testid="settings-site-url-input" value={siteForm.site_url || ""} onChange={(e) => setSiteForm({ ...siteForm, site_url: e.target.value })} placeholder="https://thisthatforme.com" className="input" />
              <p className="mt-2 text-[11px] text-[#5C5547]">
                Submit <code className="bg-[#F5F2EA] px-1.5 py-0.5 rounded">/api/sitemap.xml</code> to Google Search Console so they can crawl every product page.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href={siteForm.site_url ? `${siteForm.site_url.replace(/\/$/, "")}/api/sitemap.xml` : "/api/sitemap.xml"}
                  target="_blank" rel="noreferrer"
                  data-testid="settings-sitemap-preview"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5F2EA] text-xs text-[#2C2924] hover:bg-[#EAE4D3]">
                  Preview sitemap.xml
                </a>
                <a
                  href={`https://search.google.com/search-console/sitemaps?resource_id=${encodeURIComponent(siteForm.site_url || "https://thisthatforme.com")}`}
                  target="_blank" rel="noreferrer"
                  data-testid="settings-gsc-submit"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--accent)] text-xs text-white font-semibold hover:opacity-90">
                  Open Google Search Console →
                </a>
              </div>
            </Field>

            <div className="sm:col-span-2 mt-4 pt-4 border-t border-[#EAE4D3]">
              <div className="uppercase text-[10px] tracking-[0.2em] text-[var(--accent)] mb-3">Top announcement banner (Tiffany-style)</div>
            </div>
            <Field label="Banner enabled?" testid="settings-banner-enabled">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!siteForm.banner_enabled}
                  onChange={(e) => setSiteForm({ ...siteForm, banner_enabled: e.target.checked })} />
                Show the dark gold banner at top of every page
              </label>
            </Field>
            <Field label="Banner text" testid="settings-banner-text">
              <input value={siteForm.banner_text || ""} onChange={(e) => setSiteForm({ ...siteForm, banner_text: e.target.value })}
                placeholder="e.g. Complimentary engraving on jewelry over $500 · Free shipping over $200" className="input" />
            </Field>
            <Field label="Banner click-through link (optional)" testid="settings-banner-link">
              <input value={siteForm.banner_link || ""} onChange={(e) => setSiteForm({ ...siteForm, banner_link: e.target.value })}
                placeholder="https://… or /shop?category=jewelry" className="input" />
            </Field>

            <div className="sm:col-span-2 mt-4 pt-4 border-t border-[#EAE4D3]">
              <div className="uppercase text-[10px] tracking-[0.2em] text-[var(--accent)] mb-1">Spotlight banner (Tiffany-style hero image)</div>
              <p className="text-xs text-[#5C5547]">A large image with overlay headline + CTA that appears right under the header on every page. Perfect for new drops, sales, holidays, or announcements. Toggle off when you don't have a campaign running.</p>
            </div>
            <Field label="Spotlight banner enabled?" testid="settings-promo-enabled">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!siteForm.promo_banner_enabled}
                  onChange={(e) => setSiteForm({ ...siteForm, promo_banner_enabled: e.target.checked })}
                  data-testid="settings-promo-enabled-input" />
                Show on every page
              </label>
            </Field>
            <Field label="Text alignment" testid="settings-promo-align">
              <select value={siteForm.promo_banner_align || "left"}
                onChange={(e) => setSiteForm({ ...siteForm, promo_banner_align: e.target.value })}
                data-testid="settings-promo-align-input"
                className="input">
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </Field>
            <Field label="Banner image (wide — 1600×500 or larger)" testid="settings-promo-image" full>
              <ImageUpload
                value={siteForm.promo_banner_image || ""}
                onChange={(url) => setSiteForm({ ...siteForm, promo_banner_image: url })}
                label="Upload banner image"
                testid="settings-promo-image-upload"
              />
              <input
                value={siteForm.promo_banner_image || ""}
                onChange={(e) => setSiteForm({ ...siteForm, promo_banner_image: e.target.value })}
                placeholder="…or paste an image URL"
                data-testid="settings-promo-image-input"
                className="input mt-2"
              />
            </Field>
            <Field label="Eyebrow text (small label above headline)" testid="settings-promo-eyebrow">
              <input value={siteForm.promo_banner_eyebrow || ""}
                onChange={(e) => setSiteForm({ ...siteForm, promo_banner_eyebrow: e.target.value })}
                data-testid="settings-promo-eyebrow-input"
                placeholder="e.g. Just dropped · Holiday edit · 24-hour sale" className="input" />
            </Field>
            <Field label="Headline" testid="settings-promo-title">
              <input value={siteForm.promo_banner_title || ""}
                onChange={(e) => setSiteForm({ ...siteForm, promo_banner_title: e.target.value })}
                data-testid="settings-promo-title-input"
                placeholder="e.g. The Eternal Collection" className="input" />
            </Field>
            <Field label="Subtitle (one short line)" testid="settings-promo-subtitle" full>
              <input value={siteForm.promo_banner_subtitle || ""}
                onChange={(e) => setSiteForm({ ...siteForm, promo_banner_subtitle: e.target.value })}
                data-testid="settings-promo-subtitle-input"
                placeholder="e.g. Hand-set solitaires, complimentary engraving over $500." className="input" />
            </Field>
            <Field label="CTA button label" testid="settings-promo-cta-label">
              <input value={siteForm.promo_banner_cta_label || ""}
                onChange={(e) => setSiteForm({ ...siteForm, promo_banner_cta_label: e.target.value })}
                data-testid="settings-promo-cta-label-input"
                placeholder="e.g. Shop the collection" className="input" />
            </Field>
            <Field label="CTA button link" testid="settings-promo-cta-link">
              <input value={siteForm.promo_banner_cta_link || ""}
                onChange={(e) => setSiteForm({ ...siteForm, promo_banner_cta_link: e.target.value })}
                data-testid="settings-promo-cta-link-input"
                placeholder="/shop?category=jewelry or https://…" className="input" />
            </Field>
            <Field label="Instagram (main)" testid="settings-ig-main">
              <input value={siteForm.instagram_main} onChange={(e) => setSiteForm({ ...siteForm, instagram_main: e.target.value })} className="input" />
            </Field>
            <Field label="Instagram (secondary — Tiny Tunes)" testid="settings-ig-second">
              <input value={siteForm.instagram_secondary} onChange={(e) => setSiteForm({ ...siteForm, instagram_secondary: e.target.value })} className="input" />
            </Field>
            <Field label="Instagram (personal)" testid="settings-ig-personal">
              <input value={siteForm.instagram_personal || ""} onChange={(e) => setSiteForm({ ...siteForm, instagram_personal: e.target.value })}
                placeholder="@aminahussain" className="input" />
            </Field>
            <Field label="Facebook URL" testid="settings-facebook">
              <input value={siteForm.facebook_url || ""} onChange={(e) => setSiteForm({ ...siteForm, facebook_url: e.target.value })}
                placeholder="https://facebook.com/thisthatforme" className="input" />
            </Field>
            <Field label="TikTok URL" testid="settings-tiktok">
              <input value={siteForm.tiktok_url || ""} onChange={(e) => setSiteForm({ ...siteForm, tiktok_url: e.target.value })}
                placeholder="https://tiktok.com/@thisthatforme" className="input" />
            </Field>
            <Field label="Pinterest URL" testid="settings-pinterest">
              <input value={siteForm.pinterest_url || ""} onChange={(e) => setSiteForm({ ...siteForm, pinterest_url: e.target.value })}
                placeholder="https://pinterest.com/thisthatforme" className="input" />
            </Field>
            <Field label="YouTube URL" testid="settings-youtube">
              <input value={siteForm.youtube_url || ""} onChange={(e) => setSiteForm({ ...siteForm, youtube_url: e.target.value })}
                placeholder="https://youtube.com/@thisthatforme" className="input" />
            </Field>
            <Field label="Shopify store URL" testid="settings-shopify-url">
              <input value={siteForm.shopify_store_url || ""} onChange={(e) => setSiteForm({ ...siteForm, shopify_store_url: e.target.value })}
                placeholder="https://www.thisthatforme.com" className="input" />
            </Field>
            <Field label="Etsy Shop URL" testid="settings-etsy">
              <input value={siteForm.etsy_url || ""} onChange={(e) => setSiteForm({ ...siteForm, etsy_url: e.target.value })}
                placeholder="https://etsy.com/shop/ThisThatForMe" className="input" />
            </Field>
          </div>

          <div className="mt-10">
            <h3 className="font-serif text-xl">Payment handles</h3>
            <p className="text-xs text-[#5C5547] mt-1">These show in the checkout modal so buyers know where to send money. Leave blank to disable a method.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label="PayPal handle or email" testid="settings-paypal">
                <input value={siteForm.paypal_handle || ""} onChange={(e) => setSiteForm({ ...siteForm, paypal_handle: e.target.value })}
                  placeholder="info@thisthatforme.com" className="input" />
              </Field>
              <Field label="Zelle email" testid="settings-zelle">
                <input value={siteForm.zelle_handle || ""} onChange={(e) => setSiteForm({ ...siteForm, zelle_handle: e.target.value })}
                  placeholder="thisthatforme69@gmail.com" className="input" />
              </Field>
              <Field label="Venmo handle" testid="settings-venmo">
                <input value={siteForm.venmo_handle || ""} onChange={(e) => setSiteForm({ ...siteForm, venmo_handle: e.target.value })}
                  placeholder="@thisthatforme" className="input" />
              </Field>
              <Field label="Cash App handle" testid="settings-cashapp">
                <input value={siteForm.cashapp_handle || ""} onChange={(e) => setSiteForm({ ...siteForm, cashapp_handle: e.target.value })}
                  placeholder="$thisthatforme" className="input" />
              </Field>
            </div>
          </div>

          <div className="mt-10">
            <h3 className="font-serif text-xl">Marketing & email</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Field label="Lead-magnet title (5 free prompts)" testid="settings-leadmagnet-title">
              <input value={siteForm.lead_magnet_title || ""} onChange={(e) => setSiteForm({ ...siteForm, lead_magnet_title: e.target.value })}
                placeholder="e.g. 5 free Nano Banana prompts" className="input" />
            </Field>
            <Field label="Lead-magnet download URL" testid="settings-leadmagnet-url">
              <input value={siteForm.lead_magnet_url || ""} onChange={(e) => setSiteForm({ ...siteForm, lead_magnet_url: e.target.value })}
                placeholder="https://…/free-5-prompts.pdf" className="input" />
            </Field>
            <Field label="Resend API key (for sending emails)" testid="settings-resend-key" full>
              <input type="password" value={siteForm.resend_api_key || ""}
                onChange={(e) => setSiteForm({ ...siteForm, resend_api_key: e.target.value })}
                placeholder="re_xxxxxxxxxxx — get one at resend.com/api-keys" className="input" />
            </Field>
            <Field label="From email (verified Resend domain)" testid="settings-from-email">
              <input value={siteForm.from_email || ""}
                onChange={(e) => setSiteForm({ ...siteForm, from_email: e.target.value })}
                placeholder="hello@thisthatforme.shop" className="input" />
            </Field>
            </div>
          </div>

          <div className="mt-10">
            <h3 className="font-serif text-xl">Print-on-demand integrations</h3>
            <p className="text-xs text-[#5C5547] mt-1">Tokens are stored privately. Use the Integrations tab to test the connection.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label="Printify API token" testid="settings-printify-token" full>
                <input type="password" value={siteForm.printify_api_token || ""}
                  onChange={(e) => setSiteForm({ ...siteForm, printify_api_token: e.target.value })}
                  placeholder="paste your Printify token" className="input" />
              </Field>
              <Field label="Printify shop ID" testid="settings-printify-shop">
                <input value={siteForm.printify_shop_id || ""}
                  onChange={(e) => setSiteForm({ ...siteForm, printify_shop_id: e.target.value })}
                  placeholder="e.g. 12345678" className="input" />
              </Field>
              <Field label="Printful API token" testid="settings-printful-token" full>
                <input type="password" value={siteForm.printful_api_token || ""}
                  onChange={(e) => setSiteForm({ ...siteForm, printful_api_token: e.target.value })}
                  placeholder="paste your Printful token" className="input" />
              </Field>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <button type="submit" disabled={savingSite} data-testid="settings-save"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm disabled:opacity-60">
              <Save className="w-4 h-4" /> {savingSite ? "Saving…" : "Save settings"}
            </button>
          </div>

          <style>{`.input { width:100%; padding:.75rem 1rem; border-radius:.75rem; background:#F5F2EA; border:1px solid #EAE4D3; font-size:.875rem; outline:none; }
          .input:focus { box-shadow: 0 0 0 2px var(--accent); }`}</style>
        </form>
        </div>
      )}

      {tab === "inquiries" && (
        <div data-testid="inquiries-tab" className="space-y-3">
          {inquiries.length === 0 && (
            <div className="text-sm text-[#5C5547]">No private viewing requests yet.</div>
          )}
          {inquiries.map((i) => (
            <div key={i.id} data-testid={`inquiry-${i.id}`}
              className="rounded-2xl border border-[#EAE4D3] p-5 bg-[#FDFBF7]">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#2C2924]">{i.product_title}{i.variant_name ? ` — ${i.variant_name}` : ""}</div>
                  <div className="text-xs text-[#5C5547] mt-0.5">
                    {i.customer_name} · <a className="underline" href={`mailto:${i.customer_email}`}>{i.customer_email}</a>{i.customer_phone ? ` · ${i.customer_phone}` : ""}
                  </div>
                  <div className="mt-2 text-sm text-[#2C2924] whitespace-pre-line">"{i.message}"</div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.15em]">
                    <span className={i.status === "new" ? "text-[var(--accent)]" : i.status === "responded" ? "text-green-700" : "text-[#5C5547]"}>{i.status}</span>
                    <span className="ml-3 text-[#5C5547] normal-case tracking-normal">{new Date(i.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <a href={`mailto:${i.customer_email}?subject=Re: ${encodeURIComponent(i.product_title)}`}
                    data-testid={`inquiry-reply-${i.id}`}
                    className="px-4 py-2 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs text-center">Reply by email</a>
                  {i.status !== "responded" && (
                    <button onClick={async () => { await api.put(`/inquiries/${i.id}`, { status: "responded" }); refresh(); toast.success("Marked responded."); }}
                      data-testid={`inquiry-responded-${i.id}`}
                      className="px-4 py-2 rounded-full bg-[var(--accent)] text-white text-xs">Mark responded</button>
                  )}
                  {i.status !== "closed" && (
                    <button onClick={async () => { await api.put(`/inquiries/${i.id}`, { status: "closed" }); refresh(); }}
                      data-testid={`inquiry-close-${i.id}`}
                      className="px-4 py-2 rounded-full bg-[#F5F2EA] text-[#2C2924] text-xs">Close</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "testimonials" && (
        <div data-testid="testimonials-tab">
          <div className="flex justify-end mb-6">
            <button onClick={startNewT} data-testid="add-testimonial-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent)] text-white hover:opacity-90 text-sm">
              <Plus className="w-4 h-4" /> New testimonial
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {testimonials.map((t) => (
              <div key={t.id} data-testid={`testimonial-row-${t.id}`}
                className="p-5 rounded-2xl bg-[#FDFBF7] border-2 border-[var(--accent)]/25">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#5C5547]">{t.source} · {t.rating}★</div>
                  <div className="flex gap-1">
                    <button onClick={() => startEditT(t)} className="p-2 rounded-lg hover:bg-[#F5F2EA]"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => removeT(t.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="mt-3 text-sm">"{t.content}"</p>
                <div className="mt-3 text-xs text-[#5C5547]">— {t.name} {t.featured && "· featured"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "hero" && (
        <form onSubmit={saveHeroTiles} data-testid="hero-tab" className="bg-[#FDFBF7] rounded-3xl border-2 border-[var(--accent-strong)]/40 p-8">
          <h2 className="font-serif text-2xl">Hero tiles</h2>
          <p className="text-sm text-[#5C5547] mt-1">The 4 clickable image boxes on the home hero. Pick a product or set a custom image + link.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
            {heroTiles.map((t, idx) => (
              <div key={t.position || idx} className="p-5 rounded-2xl bg-[#F5F2EA] border border-[#EAE4D3]" data-testid={`hero-tile-form-${idx + 1}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-serif text-lg">Tile {idx + 1}</div>
                  {t.image_url && <img src={t.image_url} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                </div>
                <div className="space-y-2">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#5C5547]">Pick a product (optional)</label>
                  <select value={t.product_id || ""} onChange={(e) => pickProductForTile(idx, e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#EAE4D3] text-sm">
                    <option value="">— custom (no product) —</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                  <input value={t.image_url} onChange={(e) => setTile(idx, { image_url: e.target.value })}
                    placeholder="Image URL" className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#EAE4D3] text-sm" />
                  <input value={t.label} onChange={(e) => setTile(idx, { label: e.target.value })}
                    placeholder="Label / caption" className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#EAE4D3] text-sm" />
                  <input value={t.link} onChange={(e) => setTile(idx, { link: e.target.value })}
                    placeholder="Link (e.g. /shop?category=beauty or https://…)" className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#EAE4D3] text-sm" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex justify-end">
            <button type="submit" data-testid="hero-save"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">
              <Save className="w-4 h-4" /> Save hero tiles
            </button>
          </div>
        </form>
      )}

      {tab === "content" && (
        <form onSubmit={saveSite} data-testid="content-tab" className="space-y-8">
          <div className="bg-[#FDFBF7] rounded-3xl thick-gold p-6">
            <h3 className="font-serif text-2xl">Homepage hero</h3>
            <p className="text-sm text-[#5C5547] mt-1">Edit the big headline, subtitle, and call-to-action buttons.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <Field label="Eyebrow label" testid="hero-eyebrow">
                <input value={siteForm.hero_eyebrow || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_eyebrow: e.target.value })} className="input" />
              </Field>
              <Field label="Heading line 1" testid="hero-h1">
                <input value={siteForm.hero_heading_part1 || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_heading_part1: e.target.value })} className="input" />
              </Field>
              <Field label="Heading line 2 (gold italic accent)" testid="hero-h2">
                <input value={siteForm.hero_heading_accent || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_heading_accent: e.target.value })} className="input" />
              </Field>
              <Field label="Heading line 3" testid="hero-h3">
                <input value={siteForm.hero_heading_part2 || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_heading_part2: e.target.value })} className="input" />
              </Field>
              <Field label="Subtitle paragraph (use {brand} for brand name)" testid="hero-subtitle" full>
                <textarea rows={3} value={siteForm.hero_subtitle || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_subtitle: e.target.value })} className="input" />
              </Field>
              <Field label="Primary button label" testid="hero-cta1-label">
                <input value={siteForm.hero_cta_primary_label || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_cta_primary_label: e.target.value })} className="input" />
              </Field>
              <Field label="Primary button link" testid="hero-cta1-link">
                <input value={siteForm.hero_cta_primary_link || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_cta_primary_link: e.target.value })} placeholder="/shop" className="input" />
              </Field>
              <Field label="Secondary button label" testid="hero-cta2-label">
                <input value={siteForm.hero_cta_secondary_label || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_cta_secondary_label: e.target.value })} className="input" />
              </Field>
              <Field label="Secondary button link" testid="hero-cta2-link">
                <input value={siteForm.hero_cta_secondary_link || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_cta_secondary_link: e.target.value })} className="input" />
              </Field>
              <Field label="Stat: drops" testid="stat-drops">
                <input value={siteForm.stat_drops || ""} onChange={(e) => setSiteForm({ ...siteForm, stat_drops: e.target.value })} className="input" />
              </Field>
              <Field label="Stat: rating" testid="stat-rating">
                <input value={siteForm.stat_rating || ""} onChange={(e) => setSiteForm({ ...siteForm, stat_rating: e.target.value })} className="input" />
              </Field>
              <Field label="Stat: buyers" testid="stat-buyers">
                <input value={siteForm.stat_buyers || ""} onChange={(e) => setSiteForm({ ...siteForm, stat_buyers: e.target.value })} className="input" />
              </Field>
            </div>
          </div>

          <div className="bg-[#FDFBF7] rounded-3xl thick-gold p-6">
            <h3 className="font-serif text-2xl">Info pages</h3>
            <p className="text-sm text-[#5C5547] mt-1">Edit the text on About, Privacy, Terms, FAQ, and Shipping pages. Separate paragraphs with a blank line.</p>
            <div className="grid grid-cols-1 gap-4 mt-5">
              <Field label="About title" testid="about-title">
                <input value={siteForm.about_title || ""} onChange={(e) => setSiteForm({ ...siteForm, about_title: e.target.value })} className="input" />
              </Field>
              <Field label="About body" testid="about-body" full>
                <textarea rows={5} value={siteForm.about_body || ""} onChange={(e) => setSiteForm({ ...siteForm, about_body: e.target.value })} className="input" />
              </Field>
              <Field label="Privacy Policy body" testid="privacy-body" full>
                <textarea rows={5} value={siteForm.privacy_body || ""} onChange={(e) => setSiteForm({ ...siteForm, privacy_body: e.target.value })} className="input" />
              </Field>
              <Field label="Terms of Service body" testid="terms-body" full>
                <textarea rows={5} value={siteForm.terms_body || ""} onChange={(e) => setSiteForm({ ...siteForm, terms_body: e.target.value })} className="input" />
              </Field>
              <Field label="FAQ body" testid="faq-body" full>
                <textarea rows={5} value={siteForm.faq_body || ""} onChange={(e) => setSiteForm({ ...siteForm, faq_body: e.target.value })} className="input" />
              </Field>
              <Field label="Shipping & Returns body" testid="shipping-body" full>
                <textarea rows={5} value={siteForm.shipping_body || ""} onChange={(e) => setSiteForm({ ...siteForm, shipping_body: e.target.value })} className="input" />
              </Field>
            </div>
          </div>

          <div className="flex justify-end">
            <button type="submit" data-testid="save-content-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm font-medium">
              <Save className="w-4 h-4" /> Save content
            </button>
          </div>
        </form>
      )}


      {tab === "recovery" && (
        <div data-testid="recovery-tab" className="space-y-6">
          <div className="bg-[#FDFBF7] rounded-3xl thick-gold p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="font-serif text-2xl">Cart recovery</h3>
              <p className="text-sm text-[#5C5547] mt-1">Send a 10% off code to abandoned-cart visitors who left items 24h+ ago.</p>
            </div>
            <button onClick={sendRecovery} disabled={recoverLoading} data-testid="send-recovery-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-60"
              style={{ background: "var(--accent-strong)", color: "#000" }}>
              <Mail className="w-4 h-4" /> {recoverLoading ? "Sending…" : "Send recovery emails"}
            </button>
          </div>

          <div data-testid="wishlist-card" className="bg-[#FDFBF7] rounded-3xl thick-gold p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="font-serif text-2xl">Wishlist reminders</h3>
              <p className="text-sm text-[#5C5547] mt-1">Send "still thinking?" emails to anyone who saved a product 3+ days ago. Wishlists: {wishlists.length}</p>
            </div>
            <button onClick={sendWishlistReminders} disabled={wishlistLoading} data-testid="send-wishlist-reminders-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-60"
              style={{ background: "var(--accent-strong)", color: "#000" }}>
              <Mail className="w-4 h-4" /> {wishlistLoading ? "Sending…" : "Send wishlist reminders"}
            </button>
          </div>

          <div data-testid="upsell-card" className="bg-[#FDFBF7] rounded-3xl thick-gold p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-serif text-2xl">Post-purchase upsell</h3>
                <p className="text-sm text-[#5C5547] mt-1">
                  Auto-runs daily. Customers with paid orders 3+ days old get a 15% off code and 4 hand-picked product recommendations.
                </p>
              </div>
              <button onClick={runUpsellPass} disabled={upsellLoading} data-testid="run-upsell-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-60"
                style={{ background: "var(--accent-strong)", color: "#000" }}>
                <Mail className="w-4 h-4" /> {upsellLoading ? "Running…" : "Run pass now"}
              </button>
            </div>
            {upsellStatus && (
              <div data-testid="upsell-status" className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Paid orders" value={upsellStatus.total_paid_orders} />
                <Stat label="Upsells sent" value={upsellStatus.upsells_sent} />
                <Stat label="Pending now" value={upsellStatus.pending_now} />
                <Stat label="Resend key"
                  value={upsellStatus.resend_configured ? "Active" : "Not set"}
                  tone={upsellStatus.resend_configured ? "ok" : "warn"} />
              </div>
            )}
            {upsellStatus && !upsellStatus.resend_configured && (
              <div className="mt-4 text-xs text-[#5C5547] bg-[#F5F2EA] rounded-xl p-3">
                Add your Resend API key in <strong>Site Settings</strong> to actually deliver emails. Pass will retry sending automatically once the key is set.
              </div>
            )}
          </div>

          <div className="bg-[#FDFBF7] rounded-3xl thick-gold overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F5F2EA] uppercase text-[10px] tracking-[0.2em] text-[#5C5547]">
              <div className="col-span-5">Email</div>
              <div className="col-span-2">Items</div>
              <div className="col-span-3">Last seen</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {carts.length === 0 ? (
              <div className="p-10 text-center text-[#5C5547]">No abandoned carts yet.</div>
            ) : carts.map((c) => (
              <div key={c.email} data-testid={`abandoned-${c.email}`}
                className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-[#EAE4D3] items-center text-sm">
                <div className="col-span-5">{c.email}</div>
                <div className="col-span-2">{c.item_count}</div>
                <div className="col-span-3 text-xs text-[#5C5547]">{new Date(c.last_seen_at).toLocaleString()}</div>
                <div className="col-span-2 text-right">
                  <span className={`uppercase tracking-[0.15em] text-xs ${c.recovered ? "text-green-700" : "text-[var(--accent-strong)]"}`}>
                    {c.recovered ? "recovered" : "pending"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "reports" && <ReportsPanel />}
      {tab === "banners" && <BannersPanel />}


      {tab === "integrations" && (
        <div data-testid="integrations-tab" className="space-y-6">
          <div className="bg-[#FDFBF7] rounded-3xl border-2 border-[var(--accent)]/25 p-6">
            <h3 className="font-serif text-2xl">Printify</h3>
            <p className="text-sm text-[#5C5547] mt-1">For T-shirts and merch printing. Add token + shop ID in Settings.</p>
            <div className="mt-4 flex gap-2 flex-wrap">
              <button onClick={() => runIntegration("/integrations/printify/test", "Printify connection")} data-testid="printify-test"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">
                <RefreshCw className={`w-4 h-4 ${intLoading === "Printify connection" ? "animate-spin" : ""}`} /> Test connection
              </button>
              <button onClick={() => runIntegration("/integrations/printify/products", "Printify products")} data-testid="printify-products"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent)] text-white text-sm">
                List products
              </button>
              <button onClick={() => importCatalog("printify")} data-testid="printify-import"
                disabled={intLoading === "Printify import"}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent-strong)] text-black text-sm font-semibold disabled:opacity-60">
                <RefreshCw className={`w-4 h-4 ${intLoading === "Printify import" ? "animate-spin" : ""}`} />
                {intLoading === "Printify import" ? "Importing…" : "Import all into catalog"}
              </button>
            </div>
          </div>

          <div className="bg-[#FDFBF7] rounded-3xl border-2 border-[var(--accent)]/25 p-6">
            <h3 className="font-serif text-2xl">Printful</h3>
            <p className="text-sm text-[#5C5547] mt-1">Alternate print-on-demand for merch & accessories.</p>
            <div className="mt-4 flex gap-2 flex-wrap">
              <button onClick={() => runIntegration("/integrations/printful/test", "Printful connection")} data-testid="printful-test"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">
                <RefreshCw className={`w-4 h-4 ${intLoading === "Printful connection" ? "animate-spin" : ""}`} /> Test connection
              </button>
              <button onClick={() => runIntegration("/integrations/printful/products", "Printful products")} data-testid="printful-products"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent)] text-white text-sm">
                List products
              </button>
              <button onClick={() => importCatalog("printful")} data-testid="printful-import"
                disabled={intLoading === "Printful import"}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent-strong)] text-black text-sm font-semibold disabled:opacity-60">
                <RefreshCw className={`w-4 h-4 ${intLoading === "Printful import" ? "animate-spin" : ""}`} />
                {intLoading === "Printful import" ? "Importing…" : "Import all into catalog"}
              </button>
            </div>
          </div>

          {intResult && (
            <div className="bg-[#2C2924] text-[#F3E5AB] rounded-3xl p-6">
              <div className="text-xs uppercase tracking-[0.25em] mb-3">{intResult.label}</div>
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">{JSON.stringify(intResult.data, null, 2)}</pre>
            </div>
          )}
        </div>
      )}

      {tEditing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <form onSubmit={saveT} className="bg-[#FDFBF7] rounded-3xl border border-[#EAE4D3] w-full max-w-lg p-8 relative" data-testid="testimonial-form">
            <button type="button" onClick={() => setTEditing(null)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F5F2EA]"><X className="w-4 h-4" /></button>
            <h2 className="font-serif text-2xl mb-4">{tEditing === "new" ? "New testimonial" : "Edit testimonial"}</h2>
            <div className="space-y-3">
              <input required placeholder="Name / handle" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <select value={tForm.source} onChange={(e) => setTForm({ ...tForm, source: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm">
                <option value="website">Website (verified buyer)</option>
                <option value="instagram">Instagram</option>
                <option value="etsy">Etsy</option>
                <option value="tiktok">TikTok</option>
                <option value="google">Google</option>
              </select>
              <input type="number" min="1" max="5" placeholder="Rating (1-5)" value={tForm.rating} onChange={(e) => setTForm({ ...tForm, rating: parseInt(e.target.value || 5) })} className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <textarea required rows={4} placeholder="What did they say?" value={tForm.content} onChange={(e) => setTForm({ ...tForm, content: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <input placeholder="Avatar URL (optional)" value={tForm.avatar_url || ""} onChange={(e) => setTForm({ ...tForm, avatar_url: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <input placeholder="Source link (optional)" value={tForm.link || ""} onChange={(e) => setTForm({ ...tForm, link: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={tForm.featured} onChange={(e) => setTForm({ ...tForm, featured: e.target.checked })} /> Featured on homepage
              </label>
            </div>
            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setTEditing(null)} className="flex-1 py-3 rounded-full bg-[#F5F2EA] text-sm">Cancel</button>
              <button type="submit" className="flex-1 py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">Save</button>
            </div>
          </form>
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <form onSubmit={save} data-testid="product-form"
            className="bg-[#FDFBF7] rounded-3xl border border-[#EAE4D3] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 relative">
            <button type="button" onClick={() => setEditing(null)} data-testid="close-form"
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F5F2EA]"><X className="w-4 h-4" /></button>
            <h2 className="font-serif text-2xl mb-6">{editing === "new" ? "New product" : "Edit product"}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="form-title" className="sm:col-span-2 px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <textarea required placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="form-description" className="sm:col-span-2 px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <input type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                data-testid="form-price" className="px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="form-category" className="px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm">
                {SHOPPABLE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <div className="sm:col-span-2 p-4 rounded-2xl border border-[#EAE4D3] bg-[#F5F2EA]/60" data-testid="form-cover-block">
                <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-3">Main product image (cover)</div>
                <ImageUpload
                  value={form.image_url || ""}
                  onChange={(url) => setForm({ ...form, image_url: url })}
                  label="Upload cover image"
                  testid="form-cover-upload"
                  accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                  allowedTypes={["image/jpeg", "image/png", "image/webp"]}
                  large
                />
                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-[#EAE4D3]" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#5C5547]">or</span>
                  <div className="flex-1 h-px bg-[#EAE4D3]" />
                </div>
                <input placeholder="Main image URL (paste a public image link)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  data-testid="form-image" className="w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] text-sm" />
                <p className="mt-2 text-[11px] text-[#5C5547]">JPG, PNG or WebP · max 5MB. Uploads are stored permanently and the link is filled in for you.</p>
              </div>
              <div className="sm:col-span-2" data-testid="form-gallery">
                <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-2">
                  Additional images (gallery — shows as thumbnails)
                </div>
                <div className="space-y-2">
                  {(form.images || []).map((img, idx) => (
                    <div key={`gallery-row-${idx}`} className="flex gap-2 items-center">
                      {img && <img src={img} alt={`gallery ${idx + 1}`} className="w-12 h-12 rounded-lg object-cover border border-[#EAE4D3]" />}
                      <input
                        value={img}
                        onChange={(e) => {
                          const next = [...(form.images || [])];
                          next[idx] = e.target.value;
                          setForm({ ...form, images: next });
                        }}
                        placeholder="https://image-url.jpg"
                        data-testid={`form-gallery-input-${idx}`}
                        className="flex-1 px-4 py-2.5 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm"
                      />
                      <button type="button"
                        onClick={() => setForm({ ...form, images: (form.images || []).filter((_, i) => i !== idx) })}
                        data-testid={`form-gallery-remove-${idx}`}
                        className="px-3 py-2 rounded-full bg-red-50 text-red-700 text-xs">Remove</button>
                    </div>
                  ))}
                  <button type="button"
                    onClick={() => setForm({ ...form, images: [...(form.images || []), ""] })}
                    data-testid="form-gallery-add"
                    className="px-4 py-2 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs">
                    + Add image
                  </button>
                </div>
              </div>
              <input placeholder="Platform (e.g. nano-banana, gpt)" value={form.platform || ""} onChange={(e) => setForm({ ...form, platform: e.target.value })}
                data-testid="form-platform" className="px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_digital} onChange={(e) => setForm({ ...form, is_digital: e.target.checked })}
                  data-testid="form-is-digital" /> Digital product
              </label>
              <input placeholder="Download URL (digital)" value={form.download_url || ""} onChange={(e) => setForm({ ...form, download_url: e.target.value })}
                data-testid="form-download" className="sm:col-span-2 px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <div className="sm:col-span-2 p-4 rounded-2xl border border-[var(--accent)]/40 bg-[var(--accent)]/5" data-testid="form-external-checkout-block">
                <label className="block uppercase text-[10px] tracking-[0.2em] text-[var(--accent)] mb-2">Publishing Studio Sales Link</label>
                <input type="url" placeholder="https://shop.thisthatforme.com/go/…" value={form.external_checkout_url || ""}
                  onChange={(e) => setForm({ ...form, external_checkout_url: e.target.value })}
                  data-testid="form-external-checkout-url"
                  className="w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] text-sm" />
                <p className="mt-2 text-[11px] text-[#5C5547] leading-relaxed">
                  Paste the Website Sales Link from This That For Me Publishing Studio. When present, Buy Now sends the customer to Publishing Studio for secure checkout and delivery.
                </p>
              </div>
              <input placeholder="Shopify URL (physical)" value={form.shopify_url || ""} onChange={(e) => setForm({ ...form, shopify_url: e.target.value })}
                data-testid="form-shopify" className="px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <input placeholder="Affiliate URL" value={form.affiliate_url || ""} onChange={(e) => setForm({ ...form, affiliate_url: e.target.value })}
                data-testid="form-affiliate" className="px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <input placeholder="Video embed URL (YouTube)" value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                data-testid="form-video" className="sm:col-span-2 px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <input placeholder="Podcast embed URL (Spotify/Apple)" value={form.podcast_url || ""} onChange={(e) => setForm({ ...form, podcast_url: e.target.value })}
                data-testid="form-podcast" className="sm:col-span-2 px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  data-testid="form-featured" /> Featured on homepage
              </label>
            </div>

            <OptionsEditor form={form} setForm={setForm} />

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setEditing(null)} className="flex-1 py-3 rounded-full bg-[#F5F2EA] text-sm">Cancel</button>
              <button type="submit" data-testid="form-save"
                className="flex-1 py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function TabBtn({ id, tab, setTab, icon: Icon, label }) {
  const active = tab === id;
  return (
    <button onClick={() => setTab(id)} data-testid={`tab-${id}`}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm border ${active ? "bg-[#2C2924] text-[#FDFBF7] border-[#2C2924]" : "border-[#EAE4D3] text-[#5C5547]"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

// ---------- Product OptionsEditor (Templates + free-form) ----------
const OPTION_TEMPLATES = {
  none: { label: "No options", groups: [] },
  apparel: {
    label: "Apparel (Size / Color)",
    groups: [
      { name: "Color", kind: "color", values: [{ value: "White" }, { value: "Black" }, { value: "Beige" }] },
      { name: "Size", kind: "text", values: [{ value: "S" }, { value: "M" }, { value: "L" }, { value: "XL" }] },
    ],
  },
  jewelry: {
    label: "Jewelry (Tiffany-style: Ring Size / Metal / Stone / Carat / Quality)",
    groups: [
      { name: "Ring Size", kind: "text", values: [
        { value: "3" }, { value: "3.5" }, { value: "4" }, { value: "4.5" },
        { value: "5" }, { value: "5.5" }, { value: "6" }, { value: "6.5" },
        { value: "7" }, { value: "7.5" }, { value: "8" }, { value: "8.5" },
        { value: "9" }, { value: "9.5" }, { value: "10" }, { value: "10.5" }, { value: "11" },
      ]},
      { name: "Metal", kind: "color", values: [
        { value: "Platinum", hex: "#e5e4e2" },
        { value: "18K Yellow Gold", hex: "#e6c34d" },
        { value: "18K Rose Gold", hex: "#e0a89a" },
        { value: "18K White Gold", hex: "#f1ede4" },
        { value: "14K Yellow Gold", hex: "#dfb841" },
        { value: "14K Rose Gold", hex: "#d99a8c" },
        { value: "14K White Gold", hex: "#ece7da" },
        { value: "Sterling Silver", hex: "#c9c9c9" },
      ]},
      { name: "Stone", kind: "text", values: [
        { value: "Diamond" }, { value: "Lab-Grown Diamond" },
        { value: "Moissanite" }, { value: "Cubic Zirconia" },
        { value: "Sapphire" }, { value: "Emerald" }, { value: "Ruby" }, { value: "Pearl" },
      ]},
      { name: "Diamond Shape", kind: "text", values: [
        { value: "Round" }, { value: "Princess" }, { value: "Oval" },
        { value: "Cushion" }, { value: "Emerald" }, { value: "Pear" }, { value: "Marquise" }, { value: "Heart" },
      ]},
      { name: "Carat", kind: "text", values: [
        { value: "0.25 ct" }, { value: "0.5 ct" }, { value: "0.75 ct" },
        { value: "1 ct" }, { value: "1.5 ct" }, { value: "2 ct" }, { value: "3 ct" },
      ]},
      { name: "Quality", kind: "text", values: [
        { value: "FL" }, { value: "IF" }, { value: "VVS1" }, { value: "VVS2" },
        { value: "VS1" }, { value: "VS2" }, { value: "SI1" }, { value: "SI2" },
      ]},
      { name: "Engraving", kind: "engraving", values: [], required: false },
    ],
  },
  beauty: {
    label: "Beauty (Shade / Size)",
    groups: [
      { name: "Shade", kind: "text", values: [{ value: "Fair" }, { value: "Light" }, { value: "Medium" }, { value: "Tan" }, { value: "Deep" }] },
      { name: "Size", kind: "text", values: [{ value: "30 ml" }, { value: "50 ml" }, { value: "100 ml" }] },
    ],
  },
  home: {
    label: "Home goods (Size / Color)",
    groups: [
      { name: "Size", kind: "text", values: [{ value: "Small" }, { value: "Medium" }, { value: "Large" }] },
      { name: "Color", kind: "color", values: [{ value: "Cream" }, { value: "Beige" }, { value: "Charcoal" }] },
    ],
  },
};

function OptionsEditor({ form, setForm }) {
  const groups = form.option_groups || [];
  const variants = form.variants || [];

  function applyTemplate(key) {
    const tpl = OPTION_TEMPLATES[key];
    if (!tpl) return;
    setForm({ ...form, option_groups: JSON.parse(JSON.stringify(tpl.groups)) });
  }

  function setGroup(idx, patch) {
    const next = groups.map((g, i) => (i === idx ? { ...g, ...patch } : g));
    setForm({ ...form, option_groups: next });
  }
  function removeGroup(idx) {
    setForm({ ...form, option_groups: groups.filter((_, i) => i !== idx) });
  }
  function addGroup() {
    setForm({ ...form, option_groups: [...groups, { name: "New option", kind: "text", values: [] }] });
  }
  function addValue(gIdx, val) {
    const v = (val || "").trim();
    if (!v) return;
    const g = groups[gIdx];
    if ((g.values || []).some((x) => x.value === v)) return;
    setGroup(gIdx, { values: [...(g.values || []), { value: v }] });
  }
  function removeValue(gIdx, vIdx) {
    const g = groups[gIdx];
    setGroup(gIdx, { values: g.values.filter((_, i) => i !== vIdx) });
  }

  // Variant table helpers
  function generateVariants() {
    const variantGroups = groups.filter((g) => g.kind !== "engraving");
    if (!variantGroups.length) { setForm({ ...form, variants: [] }); return; }
    const cartesian = variantGroups.reduce((acc, g) => {
      const values = (g.values || []).map((v) => v.value);
      if (!values.length) return acc;
      if (acc.length === 0) return values.map((val) => ({ [g.name]: val }));
      const out = [];
      acc.forEach((row) => values.forEach((val) => out.push({ ...row, [g.name]: val })));
      return out;
    }, []);
    const existing = new Map((form.variants || []).map((v) => [JSON.stringify(v.options || {}), v]));
    const next = cartesian.map((opts) => {
      const key = JSON.stringify(opts);
      const found = existing.get(key);
      return found || {
        id: `v-${Math.random().toString(36).slice(2, 9)}`,
        name: Object.values(opts).join(" / "),
        options: opts,
        price: form.price || 0,
        image_url: "",
      };
    });
    setForm({ ...form, variants: next });
  }
  function setVariant(idx, patch) {
    const next = variants.map((v, i) => (i === idx ? { ...v, ...patch } : v));
    setForm({ ...form, variants: next });
  }
  function removeVariant(idx) {
    setForm({ ...form, variants: variants.filter((_, i) => i !== idx) });
  }

  return (
    <div className="mt-6 border-t border-[#EAE4D3] pt-6" data-testid="options-editor">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-serif text-xl">Product options</h3>
        <div className="flex items-center gap-2">
          <select onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); e.target.value = ""; }}
            data-testid="options-template"
            className="px-3 py-2 rounded-full bg-[#F5F2EA] border border-[#EAE4D3] text-xs">
            <option value="">Apply template…</option>
            {Object.entries(OPTION_TEMPLATES).map(([key, t]) => (
              <option key={key} value={key}>{t.label}</option>
            ))}
          </select>
          <button type="button" onClick={addGroup} data-testid="add-option-group"
            className="px-3 py-2 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs">+ Add option</button>
        </div>
      </div>

      {groups.length === 0 && (
        <div className="text-xs text-[#5C5547]">No options yet. Pick a template above or click <strong>+ Add option</strong> to define custom options (Metal, Stone, Carat, Shade, etc.).</div>
      )}

      <div className="space-y-3">
        {groups.map((g, gIdx) => (
          <div key={`g-${gIdx}`} className="rounded-2xl border border-[#EAE4D3] p-4 bg-[#FDFBF7]" data-testid={`option-group-${gIdx}`}>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <input value={g.name} onChange={(e) => setGroup(gIdx, { name: e.target.value })}
                placeholder="Option name (e.g. Metal)"
                data-testid={`option-group-name-${gIdx}`}
                className="flex-1 min-w-[8rem] px-3 py-2 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              <select value={g.kind || "text"} onChange={(e) => setGroup(gIdx, { kind: e.target.value })}
                data-testid={`option-group-kind-${gIdx}`}
                className="px-3 py-2 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-xs">
                <option value="text">Buttons</option>
                <option value="color">Color swatches</option>
                <option value="engraving">Engraving (text input)</option>
              </select>
              <button type="button" onClick={() => removeGroup(gIdx)} data-testid={`remove-option-group-${gIdx}`}
                className="px-3 py-2 rounded-full bg-red-50 text-red-700 text-xs">Remove</button>
            </div>
            {g.kind === "engraving" ? (
              <div className="text-xs text-[#5C5547]">
                Buyers will see a text input here to personalize their order (e.g. initials, names, dates). No values to configure.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(g.values || []).map((v, vIdx) => (
                  <span key={`v-${vIdx}`} data-testid={`option-value-${gIdx}-${vIdx}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F5F2EA] border border-[#EAE4D3] text-xs">
                    {v.value}
                    <button type="button" onClick={() => removeValue(gIdx, vIdx)} className="ml-1 text-red-600 hover:text-red-800" title="Delete this value">×</button>
                  </span>
                ))}
                <ValueAdder onAdd={(val) => addValue(gIdx, val)} testid={`add-value-${gIdx}`} />
              </div>
            )}
          </div>
        ))}
      </div>

      {groups.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h4 className="font-serif text-lg">Variants ({variants.length})</h4>
            <button type="button" onClick={generateVariants} data-testid="generate-variants"
              className="px-3 py-2 rounded-full bg-[var(--accent)] text-white text-xs">
              {variants.length ? "Regenerate from options" : "Generate variants"}
            </button>
          </div>
          {variants.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="variants-table">
                <thead>
                  <tr className="text-left text-[#5C5547] uppercase tracking-[0.15em]">
                    {groups.filter((g) => g.kind !== "engraving").map((g) => <th key={g.name} className="py-2 pr-3">{g.name}</th>)}
                    <th className="py-2 pr-3">Price</th>
                    <th className="py-2 pr-3" title="Empty = Made to order">Stock</th>
                    <th className="py-2 pr-3">Image URL</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v, idx) => (
                    <tr key={v.id || idx} className="border-t border-[#EAE4D3]" data-testid={`variant-row-${idx}`}>
                      {groups.filter((g) => g.kind !== "engraving").map((g) => (
                        <td key={g.name} className="py-2 pr-3 align-top">{(v.options || {})[g.name] || "—"}</td>
                      ))}
                      <td className="py-2 pr-3 align-top">
                        <input type="number" step="0.01" value={v.price ?? ""}
                          onChange={(e) => setVariant(idx, { price: e.target.value === "" ? null : parseFloat(e.target.value) })}
                          data-testid={`variant-price-${idx}`}
                          className="w-20 px-2 py-1 rounded-lg bg-[#F5F2EA] border border-[#EAE4D3]" />
                      </td>
                      <td className="py-2 pr-3 align-top">
                        <input type="number" min="0" step="1" value={v.stock ?? ""}
                          placeholder="∞"
                          onChange={(e) => setVariant(idx, { stock: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                          data-testid={`variant-stock-${idx}`}
                          title="Leave blank for Made-to-order (unlimited)"
                          className="w-16 px-2 py-1 rounded-lg bg-[#F5F2EA] border border-[#EAE4D3]" />
                      </td>
                      <td className="py-2 pr-3 align-top">
                        <input value={v.image_url || ""} onChange={(e) => setVariant(idx, { image_url: e.target.value })}
                          placeholder="https://…" data-testid={`variant-image-${idx}`}
                          className="w-56 px-2 py-1 rounded-lg bg-[#F5F2EA] border border-[#EAE4D3]" />
                      </td>
                      <td className="py-2 pr-3 align-top">
                        <button type="button" onClick={() => removeVariant(idx)} data-testid={`variant-remove-${idx}`}
                          className="px-2 py-1 rounded-full bg-red-50 text-red-700">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ValueAdder({ onAdd, testid }) {
  const [v, setV] = useState("");
  return (
    <span className="inline-flex items-center gap-1">
      <input value={v} onChange={(e) => setV(e.target.value)}
        placeholder="Add value…"
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(v); setV(""); } }}
        data-testid={testid}
        className="px-3 py-1.5 rounded-full bg-[#FDFBF7] border border-[#EAE4D3] text-xs w-32" />
      <button type="button" onClick={() => { onAdd(v); setV(""); }}
        className="px-2.5 py-1 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs">Add</button>
    </span>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "sm:col-span-2" : ""} data-testid={testid}>
      <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-2">{label}</div>
      {children}
    </div>
  );
}


function Stat({ label, value, tone }) {
  const toneColor = tone === "ok" ? "text-green-700" : tone === "warn" ? "text-[var(--accent-strong)]" : "text-[#1A1815]";
  return (
    <div className="bg-[#F5F2EA] rounded-2xl p-4 border border-[#EAE4D3]">
      <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547]">{label}</div>
      <div className={`font-serif text-2xl mt-2 ${toneColor}`}>{value}</div>
    </div>
  );
}

function ChangeAdminCredentials() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.email) setNewEmail(user.email);
  }, [user]);

  async function submit(e) {
    e.preventDefault();
    if (newPw.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      const payload = { current_password: current, new_password: newPw };
      if (newEmail && newEmail.toLowerCase() !== (user?.email || "").toLowerCase()) {
        payload.new_email = newEmail.toLowerCase();
      }
      await api.post("/auth/change-password", payload);
      toast.success("Admin credentials updated. Please log in again with your new password.");
      setCurrent("");
      setNewPw("");
      setConfirmPw("");
      // Log out so they re-authenticate with the new creds
      setTimeout(async () => {
        try {
          await api.post("/auth/logout");
        } catch (logoutErr) {
          console.warn("[admin] logout failed (already logged out?):", logoutErr);
        }
        window.location.href = "/admin/login";
      }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update credentials.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} data-testid="change-admin-creds"
      className="bg-[#FDFBF7] rounded-3xl border-2 border-[var(--accent-strong)]/50 p-6">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-[var(--accent-strong)] flex items-center justify-center text-black text-sm font-bold">!</div>
        <h2 className="font-serif text-2xl">Change admin credentials</h2>
      </div>
      <p className="text-sm text-[#5C5547] mt-2">
        You're currently logged in as <strong>{user?.email}</strong>. Change your email and/or password below.
        <br />
        <span className="text-[var(--accent-strong)] font-semibold">Do this before sharing your live site publicly.</span>
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <Field label="Current password" testid="cred-current">
          <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
            placeholder="Enter current password"
            className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
        </Field>
        <Field label="New admin email" testid="cred-new-email">
          <input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
            placeholder="you@thisthatforme.com"
            className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
        </Field>
        <Field label="New password (min 8 chars)" testid="cred-new-password">
          <input type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)}
            placeholder="Strong password"
            className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
        </Field>
        <Field label="Confirm new password" testid="cred-confirm-password">
          <input type="password" required minLength={8} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
            placeholder="Retype new password"
            className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
        </Field>
      </div>
      <div className="mt-5 flex justify-end">
        <button type="submit" disabled={saving} data-testid="cred-save-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-60"
          style={{ background: "var(--accent-strong)", color: "#000" }}>
          {saving ? "Saving…" : "Update credentials"}
        </button>
      </div>
    </form>
  );
}

